// @ts-strict-ignore
import { useCallback, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useMediaQuery, useTheme } from '@mui/material';
import theme from '../../theme';
import useWindowSize from '../../services/useWindowSize';
import wrapMarkDownText from '../../services/wrapMarkDownText';
import getParentSizeFromRef from '../../services/getParentSizeFromRef';

export type DataPoint = {
    value: number;
    axis: string;
    indicatorId: string;
};

type RadarChartProps = {
    data: Array<Array<DataPoint>>;
    isPQi?: boolean;
    maxHeight?: number;
    relatedData: Array<Array<DataPoint>>;
};

function RadarChart({
    data,
    isPQi = false,
    maxHeight,
    relatedData,
}: RadarChartProps) {
    const muiTheme = useTheme();
    const isSM = useMediaQuery(muiTheme.breakpoints.down('md'), {
        // https://github.com/mui-org/material-ui/issues/21048
        noSsr: true,
    });
    const svgRef = useRef();
    const windowSize = useWindowSize();

    const render = useCallback(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('g').remove();
        const parentSize = getParentSizeFromRef(svgRef);
        if (!parentSize) {
            return;
        }
        const { parentWidth } = parentSize;
        let { parentHeight } = parentSize;
        if (maxHeight && parentHeight > maxHeight) {
            parentHeight = maxHeight;
        }

        const allData = (
            relatedData.length
                ? data.map((dataRole, index) =>
                      dataRole.concat(relatedData[index]),
                  )
                : data
        ).map((unsortedDataByRole) =>
            unsortedDataByRole.sort((a, b) => {
                if (a.indicatorId < b.indicatorId) {
                    return -1;
                }
                if (a.indicatorId > b.indicatorId) {
                    return 1;
                }
                if (a.axis < b.axis) {
                    return -1;
                }
                if (a.axis > b.axis) {
                    return 1;
                }
                return 0;
            }),
        );

        const cfg = {
            x: 0,
            y: 0,
            w: parentWidth, //Width of the circle
            h: parentHeight, //Height of the circle
            labelFontSize: isSM ? 10 : 14,
            axisLabelFontSize: isSM ? 8 : 12,
            gridColor:
                muiTheme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : '#E5E5E5',
            axisLabelColor: '#888888',
            margin: {
                top: 30,
                right: 20,
                bottom: allData[0]?.length > 16 ? 140 : 20,
                left: 2,
            }, //The margins of the SVG
            levels: isSM ? 5 : 10, //How many levels or inner circles should there be drawn
            maxValue: 100, //What is the value that the biggest circle will represent
            labelFactor: isSM ? 1.15 : 1.25, //How much farther than the radius of the outer circle should the labels be placed
            gridOverflowFactor: isSM ? 1.1 : 1.15, //How much farther than the radius of the grid-lines should extend
            wrapWidth: 60, //The number of pixels after which a label needs to be given a new line
            opacityArea: 0, //The opacity of the area of the blob
            dotRadius: isSM ? 6 : 8, //The size of the colored circles of each blob
            opacityCircles: 0, //The opacity of the circles of each blob
            strokeWidth: 2, //The width of the stroke around each blob
            roundStrokes: false, //If true the area and stroke will follow a round path (cardinal-closed)
            color: d3.scaleOrdinal<number, string>().range([
                theme.color.palePurple, //change this colors?
                theme.color.blue,
                theme.color.blueGreen,
            ]),
            dash: (i: number) => i * 2,
        };

        const isMain = (axName: string) =>
            data[0].find((ax) => ax.axis === axName);

        //If the supplied maxValue is smaller than the actual one, replace by the max in the data

        const maxValue = Math.max(
            cfg.maxValue,
            d3.max(allData, (item) => d3.max(item.map((o) => o.value))),
        );

        const allAxis = allData[0].map((i) => i.axis),
            //Names of each axis
            total = allAxis.length,
            //The number of different axes
            //Radius of the outermost circle
            radius = Math.min(
                (cfg.w - cfg.margin.left - cfg.margin.right) / (isSM ? 4 : 2.5),
                (cfg.h - cfg.margin.top - cfg.margin.bottom) / (isSM ? 4 : 2.5),
                350,
            ),
            Format = (d: any) => d;
        const angleSlice = (Math.PI * 2) / total; //The width in radians of each "slice"
        //Scale for the radius
        const rScale = d3
            .scaleLinear()
            .range([0, radius])
            .domain([0, maxValue]);

        /////////////////////////////////////////////////////////
        //////////// Create the container SVG and g /////////////
        /////////////////////////////////////////////////////////

        //Initiate the radar chart SVG
        svg.attr('height', cfg.h);
        // svg.attr('width', cfg.w).attr('height', cfg.h);

        //Append a g element
        const g = svg
            .append('g')
            .attr(
                'transform',
                'translate(' + cfg.w / 2 + ',' + cfg.h / 2 + ')',
            );
        /////////////////////////////////////////////////////////
        ////////// Glow filter for some extra pizzazz ///////////
        /////////////////////////////////////////////////////////

        //Filter for the outside glow
        const filter = g.append('defs').append('filter').attr('id', 'glow');
        filter
            .append('feGaussianBlur')
            .attr('stdDeviation', '2.5')
            .attr('result', 'coloredBlur');

        const feMerge = filter.append('feMerge');
        feMerge.append('feMergeNode').attr('in', 'coloredBlur');
        feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

        /////////////////////////////////////////////////////////
        /////////////// Draw the Circular grid //////////////////
        /////////////////////////////////////////////////////////

        //Wrapper for the grid & axes
        const axisGrid = g.append('g').attr('class', 'axisWrapper');

        //Draw the background circles
        axisGrid
            .selectAll('.levels')
            .data(d3.range(1, cfg.levels + 1).reverse())
            .enter()
            .append('circle')
            .attr('class', 'gridCircle')
            .attr('r', (d) => (radius / cfg.levels) * d)
            .style('fill', cfg.gridColor)
            .style('stroke', cfg.gridColor)
            .style('fill-opacity', cfg.opacityCircles)
            .style('filter', 'url(#glow)');

        //Text indicating at what % each level is
        axisGrid
            .selectAll('.axisLabel')
            .data(d3.range(1, cfg.levels + 1).reverse())
            .enter()
            .append('text')
            .attr('class', 'axisLabel')
            .attr('x', -10)
            .attr('text-anchor', 'end')
            .attr('y', (d) => (-d * radius) / cfg.levels)
            .attr('dy', '0.4em')
            .style('font-size', cfg.axisLabelFontSize)
            .attr('fill', cfg.axisLabelColor)
            .text((d) => d3.format('.2r')((maxValue * d) / cfg.levels));

        /////////////////////////////////////////////////////////
        //////////////////// Draw the axes //////////////////////
        /////////////////////////////////////////////////////////

        //Create the straight lines radiating outward from the center
        const axis = axisGrid
            .selectAll('.axis')
            .data(allAxis)
            .enter()
            .append('g')
            .attr('class', 'axis');
        //Append the lines
        axis.append('line')
            .attr(
                'x1',
                (_d, i) =>
                    rScale(maxValue / cfg.levels) *
                    Math.cos(angleSlice * i - Math.PI / 2),
            )
            .attr(
                'y1',
                (_d, i) =>
                    rScale(maxValue / cfg.levels) *
                    Math.sin(angleSlice * i - Math.PI / 2),
            )
            .attr(
                'x2',
                (_d, i) =>
                    rScale(maxValue * cfg.gridOverflowFactor) *
                    Math.cos(angleSlice * i - Math.PI / 2),
            )
            .attr(
                'y2',
                (_d, i) =>
                    rScale(maxValue * cfg.gridOverflowFactor) *
                    Math.sin(angleSlice * i - Math.PI / 2),
            )
            .attr('class', 'line')
            .style('stroke', cfg.gridColor)
            .style('stroke-width', '1px');

        const textX = (i: number) => {
            return (
                rScale(maxValue * cfg.labelFactor) *
                Math.cos(angleSlice * i - Math.PI / 2)
            );
        };
        const indexToRadians = (i: number) => angleSlice * i - Math.PI / 2;

        const textY = (i: number) => {
            const x = textX(i);
            const offsetFactor = Math.abs(x) < 5 ? 0.05 : 0;
            const offset = 0;

            return (
                offset +
                rScale(maxValue * (cfg.labelFactor + offsetFactor)) *
                    Math.sin(indexToRadians(i))
            );
        };

        //Append the labels at each axis
        axis.append('text')
            .text((d) => (isPQi ? d.toUpperCase() : d))
            .style('font-family', 'AvertaStandard')
            .style('font-weight', isPQi ? 'bold' : 'normal')
            .style('font-size', cfg.labelFontSize)
            .attr('class', 'legend')
            .attr('text-anchor', (_d, i) => {
                const x = Math.round(textX(i));
                if (x === 0) {
                    return 'middle';
                }
                return x > 0 ? 'start' : 'end';
            })
            .attr('dy', '0.35em')
            .attr('x', (_d, i) => textX(i))
            .attr('y', (_d, i) => {
                // Add extra offset if we have more than 16 axis (arbitrary guess)
                const x = Math.round(textX(i));
                const y = textY(i);
                if (x === 0 && allAxis.length > 16) {
                    return y > 0 ? y + 10 : y - 10;
                }
                return y;
            })
            .attr('width', (_d, i) => {
                const x = textX(i);
                return (
                    cfg.w / 2 -
                    Math.abs(x) - // cfg.margin.left -
                    (Math.round(x) > 0 ? cfg.margin.right : cfg.margin.left)
                );
            })
            .attr('height', (_d, i) => {
                return Math.abs(textY(i - 1) - textY(i + 1));
            })
            .attr('fill', (text) =>
                isMain(text)
                    ? muiTheme.palette.text.primary
                    : muiTheme.bino.color.gray,
            )
            .call(wrapMarkDownText, {
                verticalAlign: 'center',
                styles: {
                    strong: {
                        'font-size': cfg.labelFontSize + 2,
                    },
                },
            });

        /////////////////////////////////////////////////////////
        ///////////// Draw the radar chart blobs ////////////////
        /////////////////////////////////////////////////////////

        //The radial line function
        const radarLine = d3
            .lineRadial<DataPoint>()
            .curve(cfg.roundStrokes ? d3.curveCardinal : d3.curveLinear)
            .defined((d) => d && d.value !== null)
            .radius((d) => rScale(d.value))
            .angle((_d, i) => i * angleSlice);

        const radarArea = d3
            .lineRadial<DataPoint>()
            .curve(
                cfg.roundStrokes
                    ? d3.curveCardinalClosed
                    : d3.curveLinearClosed,
            )
            .radius((d) => rScale(d.value))
            .angle((_d, i) => i * angleSlice);

        //Append the backgrounds
        g.selectAll('.radarArea')
            .data(allData)
            .enter()
            .append('path')
            .attr('class', 'radarArea')
            .attr('d', radarArea)
            .style('fill', (_d, i) => cfg.color(i))
            .style('fill-opacity', cfg.opacityArea);
        // .on('mouseover', function() {
        //     //Dim all blobs
        //     d3.selectAll('.radarArea')
        //         .transition()
        //         .duration(200)
        //         .style('fill-opacity', 0.1);
        //
        //     //Bring back the hovered over blob
        //     d3.select(this)
        //         .transition()
        //         .duration(200)
        //         .style('fill-opacity', 0.7);
        // })
        // .on('mouseout', function() {
        //     //Bring back all blobs
        //     d3.selectAll('.radarArea')
        //         .transition()
        //         .duration(200)
        //         .style('fill-opacity', cfg.opacityArea);
        // });

        // lineRadial and curveLinearClosed closes any partial line, so we do the lineRadial without
        // a closed path. So get it to close, we need to add the first data-point again
        let relatedDataClosed:
            | Iterable<unknown>
            | d3.ValueFn<SVGGElement, unknown, unknown[] | Iterable<unknown>>;
        let mainDataClosed:
            | Iterable<unknown>
            | d3.ValueFn<SVGGElement, unknown, unknown[] | Iterable<unknown>>;

        if (relatedData.length) {
            mainDataClosed = data.map((pointsByRole, index) => {
                const sortedPoints = new Array(pointsByRole.length);
                for (const point of pointsByRole) {
                    const newPointIndex = allData[index].findIndex(
                        (allDataPoint) => allDataPoint.axis === point.axis,
                    );
                    sortedPoints[newPointIndex] = point;
                }
                return sortedPoints;
            });

            relatedDataClosed = relatedData.map(
                (pointsByRoleWithoutAdditionalPoints, index) => {
                    const firstMainPoint = mainDataClosed[index].find(
                        (mainPoint: any) => mainPoint,
                    );
                    const pointsByRole = [
                        mainDataClosed[index][mainDataClosed[index].length - 1],
                        ...pointsByRoleWithoutAdditionalPoints.slice(),
                        firstMainPoint,
                    ];
                    const sortedPoints = new Array(pointsByRole.length);
                    for (const point of pointsByRole) {
                        const newPointIndex = allData[index].findIndex(
                            (allDataPoint) => allDataPoint.axis === point.axis,
                        );
                        sortedPoints[newPointIndex] = point;
                    }
                    return [...sortedPoints, sortedPoints[0]];
                },
            );
        } else {
            mainDataClosed = data.map((points) => [...points, points[0]]);
        }

        // Create the outlines
        g.selectAll('.radarLine')
            .data(mainDataClosed)
            .enter()
            .append('path')
            .attr('class', 'radarLine')
            .attr('d', radarLine)
            .style('stroke-dasharray', (_d, i) => cfg.dash(i))
            .style('stroke-width', cfg.strokeWidth + 'px')
            .style('stroke', (_d, i) => {
                return cfg.color(i);
            })
            .style('fill', 'none')
            .style('filter', 'url(#glow)')
            .exit();

        // Drawing related data
        if (relatedDataClosed) {
            g.selectAll('radarLineRelated')
                .data(relatedDataClosed)
                .enter()
                .append('path')
                .attr('class', 'radarLineRelated')
                .attr('d', radarLine)
                .style('stroke-dasharray', (_d, i) => cfg.dash(i))
                .style('stroke-width', cfg.strokeWidth + 'px')
                .style('stroke', () => theme.color.lightGray)
                .style('fill', 'none')
                .style('filter', 'url(#glow)');
        }

        //Append the circles
        let dataSeries = -1;
        g.selectAll('.radarPointWrapper')
            .data(allData)
            .enter()
            .append('g')
            .selectAll('circle')
            .data((d) => d)
            .enter()
            .append('path')
            .attr(
                'd',
                d3
                    .symbol()
                    .size(cfg.dotRadius * 20)
                    .type((_d, i) => {
                        dataSeries = dataSeries + (i === 0 ? 1 : 0);
                        switch (dataSeries) {
                            case 0:
                                return d3.symbolCircle;
                            case 1:
                                return d3.symbolTriangle;
                            default:
                                return d3.symbolDiamond;
                        }
                    }),
            )
            .attr(
                'transform',
                (d, i) =>
                    'translate(' +
                    rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2) +
                    ',' +
                    rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2) +
                    ')',
            )
            .style('display', (d) => (d.value === null ? 'none' : ''))
            .style('stroke', muiTheme.palette.background.paper)
            .style('stroke-width', 2)
            .style('fill', (d, i) => {
                dataSeries += i === 0 ? 1 : 0;
                return isMain(d.axis)
                    ? cfg.color(dataSeries)
                    : theme.color.lightGray;
            })
            .style('fill-opacity', 0.8);

        /////////////////////////////////////////////////////////
        //////// Append invisible circles for tooltip ///////////
        /////////////////////////////////////////////////////////

        //Wrapper for the invisible circles on top
        const blobCircleWrapper = g
            .selectAll('.radarCircleWrapper')
            .data(allData)
            .enter()
            .append('g')
            .attr('class', 'radarCircleWrapper');

        //Set up the small tooltip for when you hover over a circle
        const tooltip = g
            .append('text')
            .attr('class', 'tooltip')
            .style('opacity', 0);

        //Append a set of invisible circles on top for the mouseover pop-up
        blobCircleWrapper
            .selectAll('.radarInvisibleCircle')
            .data((d) => d)
            .enter()
            .append('circle')
            .attr('class', 'radarInvisibleCircle')
            .attr('r', cfg.dotRadius * 1.5)
            .attr(
                'cx',
                (d, i) =>
                    rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2),
            )
            .attr(
                'cy',
                (d, i) =>
                    rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2),
            )
            .style('fill', 'none')
            .style('pointer-events', 'all')
            .on('mouseover', function (d) {
                const newX = parseFloat(d3.select(this).attr('cx')) - 10;
                const newY = parseFloat(d3.select(this).attr('cy')) - 10;

                tooltip
                    .attr('x', newX)
                    .attr('y', newY)
                    .text(Format(d.value))
                    .transition()
                    .duration(200)
                    .style('opacity', 1);
            })
            .on('mouseout', () => {
                tooltip.transition().duration(200).style('opacity', 0);
            });
    }, [
        data,
        isPQi,
        maxHeight,
        relatedData,
        isSM,
        muiTheme.palette.mode,
        muiTheme.palette.background.paper,
        muiTheme.palette.text.primary,
        muiTheme.bino.color.gray,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: We need windowSize to re-render on resize
    useEffect(render, [render, windowSize]);

    return <svg ref={svgRef} width="100%" height="100%" overflow="visible" />;
}

export default RadarChart;
